import { Draft } from 'immer';
import { getCareCostAnchorYear } from './sharedPolicyConfig';

type Claimant = NonNullable<MonthlyCareInput['claimant']>;

const MONTHS_PER_YEAR = 12;

// Expand phase care plans into per-month rows starting at `startMonthIndex`,
// tagging each with `claimant`. Pass undefined for single-life so rows stay
// byte-identical to the legacy stream (no `claimant` key). Phases are laid
// contiguously — the contiguity assumption `getCalendarPolicyYear` relies on.
function expandPhaseCarePlansToMonthlyInputs(
  phaseCarePlans: PhaseCarePlan[],
  startMonthIndex: number,
  claimant?: Claimant,
): MonthlyCareInput[] {
  const rows: MonthlyCareInput[] = [];
  let monthIndex = startMonthIndex;

  phaseCarePlans.forEach(plan => {
    const monthsInPhase = Math.floor(plan.phaseCareMonthsNeeded);
    if (monthsInPhase === 0) return;

    const monthlyHours = plan.phaseCareHoursNeeded / monthsInPhase;

    for (let monthsIntoPhase = 0; monthsIntoPhase < monthsInPhase; monthsIntoPhase++) {
      rows.push({
        monthIndex,
        carePhase: plan.carePhase,
        monthsIntoPhase,
        careHours: Math.round(monthlyHours),
        nonAdlEligibleDays: 0,
        adlEligibleDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        // Only attach the tag for joint streams so single-life rows stay
        // byte-identical to the legacy output.
        ...(claimant ? { claimant } : {}),
      });
      monthIndex++;
    }
  });

  return rows;
}

export function calcMonthlyCareInputs(mutableClient: Draft<Client>) {
  const { phaseCarePlans } = mutableClient.projectionInputs;
  mutableClient.projectionInputs.monthlyCareInputs = expandPhaseCarePlansToMonthlyInputs(
    phaseCarePlans,
    0,
  );
}

// The calendar start year of the first phase (in care-phase order) that
// contributes at least one care month. Mirrors how `calcMonthlyCareInputs`
// starts the single-life stream at the first non-zero phase. Returns null
// when no phase contributes months or no start year is predicted.
function firstContributingPhaseStartYear(client: Client): number | null {
  const firstContributing = client.projectionInputs.phaseCarePlans.find(
    plan => Math.floor(plan.phaseCareMonthsNeeded) > 0,
  );
  // No contributing phase → this spouse has no onset to anchor on (the caller
  // excludes a null from the shared anchor and gives them offset 0).
  if (!firstContributing) return null;
  // When the phase has a real predicted start year, honor it — including one
  // genuinely before the policy's premium-start year, which legitimately drives
  // the pre-policy gate. But a NULL predicted start must NOT fall back to the
  // age-derived year (`birthYear + clientPhaseStartAges`, via
  // calcClientPhaseStartYears): that can land in the past and falsely flag a
  // forward-looking projection's care as pre-policy. An unknown onset means
  // "starts today" — the cost clock's current year — matching the care-cost
  // path's getFirstContributingPhaseStartYear so the policy-year clock and
  // the cost projection agree on one onset.
  return (
    client.clientPhasePredictedStartYears?.[firstContributing.carePhase] ??
    getCareCostAnchorYear(client)
  );
}

export interface JointMonthlyCareInputs {
  // Merged, claimant-tagged, calendar-aligned stream (interleaved by monthIndex).
  monthlyCareInputs: MonthlyCareInput[];
  // Calendar year of monthIndex 0 — the earliest claim start across both
  // spouses. Null when neither spouse has a predicted start year. The joint run
  // turns this into the shared policy-year anchor (plan A3); it is the single
  // source of truth for both the stream offsets and that anchor, so they cannot
  // drift apart.
  anchorYear: number | null;
}

// Build the merged, claimant-tagged monthly stream for a joint policy from the
// session client and its partner. Returns null when there is no partner (caller
// falls back to single-life). Pure — does not write onto either client's
// `projectionInputs`. See docs/joint-policy-strategies-plan.md A2/A3/A4 for the
// calendar-anchor + interleave rationale.
export function buildJointMonthlyCareInputs(
  sessionClient: Draft<Client>,
): JointMonthlyCareInputs | null {
  const partner = sessionClient.mutableClientPartner;
  if (!partner) return null;

  const clientStartYear = firstContributingPhaseStartYear(sessionClient);
  const partnerStartYear = firstContributingPhaseStartYear(partner);

  // Anchor `monthIndex 0` at Jan 1 of the earliest claim-start *year* across both
  // spouses (`floor(anchorYear)`), and measure each spouse's offset from that
  // same calendar origin. Anchoring on the floored year — not the fractional
  // onset — is what keeps the policy-year clock correct: the row policy year is
  // `floor(anchorYear) + floor(monthIndex/12)`, which equals
  // `floor(rowCalendarYear)` only when the offset shares the floored-year origin.
  // Measuring from the fractional onset instead understated the later spouse by a
  // year whenever the inter-spouse gap straddled a calendar boundary (anchor
  // 2024.58, premium 2020: a session onset 2025 landed at monthIndex 5 with
  // `floor(5/12)=0`, pinning it to the anchor's year 5 instead of its real 6).
  // Predicted start years are fractional (prevStart + prevMonths/12), so round
  // the gap to keep monthIndex an integer clock; relative spouse timing is
  // preserved because both offsets share the origin. Integer-onset runs are
  // unchanged (`floor(2024) === 2024`). A spouse with no predicted start year —
  // or neither having one — defaults to offset 0, since we can't place an
  // unknown-timing claim in calendar time.
  const anchorYear = Math.min(clientStartYear ?? Infinity, partnerStartYear ?? Infinity);
  const anchorYearStart = Math.floor(anchorYear);
  const offsetMonths = (startYear: number | null): number =>
    startYear == null || !Number.isFinite(anchorYear)
      ? 0
      : Math.round((startYear - anchorYearStart) * MONTHS_PER_YEAR);

  const clientRows = expandPhaseCarePlansToMonthlyInputs(
    sessionClient.projectionInputs.phaseCarePlans,
    offsetMonths(clientStartYear),
    'client',
  );
  const partnerRows = expandPhaseCarePlansToMonthlyInputs(
    partner.projectionInputs.phaseCarePlans,
    offsetMonths(partnerStartYear),
    'partner',
  );

  // Interleave by monthIndex; on a tie the session client precedes the partner
  // (matches the legacy merge's client-first order, keeping shared-pool drawdown
  // deterministic). Two rows of the same spouse never share a monthIndex.
  const monthlyCareInputs = [...clientRows, ...partnerRows].sort((a, b) =>
    a.monthIndex !== b.monthIndex ? a.monthIndex - b.monthIndex : a.claimant === 'partner' ? 1 : -1,
  );

  return {
    monthlyCareInputs,
    anchorYear: Number.isFinite(anchorYear) ? anchorYear : null,
  };
}
